import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  fetchLeaderboard,
  fetchStreamerProfile,
  searchStreamers
} from "../../services/zombiecrushersApi";
import StreamerProfilePanel from "./StreamerProfilePanel";
import "../../style/ZombieCrushersSection.css";

const DEFAULT_LIMIT = 10;

function normalizeStreamer(entry) {
  if (!entry || typeof entry !== "object") {
    return null;
  }

  const userId =
    entry.userId ||
    entry.user_id ||
    entry.twitch_user_id ||
    entry.twitchId ||
    entry.twitchUserId ||
    entry.id;

  const username =
    entry.username ||
    entry.display_name ||
    entry.displayName ||
    entry.user_name ||
    entry.twitch_username ||
    entry.twitch_display_name ||
    entry.name;

  const avatar =
    entry.avatar ||
    entry.avatar_url ||
    entry.profile_image_url ||
    entry.profileImageUrl ||
    entry.profile_pic_url ||
    entry.profilePicUrl ||
    entry.image_url ||
    entry.imageUrl;

  const bestSurvivalTime =
    entry.best_survival_time ||
    entry.best_survival ||
    entry.longest_survival_time;

  const bestSurvivalSeconds =
    entry.best_survival_seconds ??
    entry.survival_seconds ??
    entry.survival ??
    entry.longest_survival_seconds ??
    null;

  const totalSessions =
    entry.total_sessions ??
    entry.sessions ??
    entry.session_count ??
    entry.totalSessions ??
    null;

  const maxKills =
    entry.max_kills ??
    entry.best_kills ??
    entry.bestKills ??
    entry.kills ??
    entry.kill_record ??
    null;

  return {
    userId,
    username: username || "Streamer senza nome",
    rank: entry.rank ?? null,
    avatar,
    maxKills,
    bestSurvivalSeconds,
    bestSurvivalTime,
    totalSessions
  };
}

function formatSurvival(entry) {
  if (entry.bestSurvivalTime) {
    return entry.bestSurvivalTime;
  }
  if (entry.bestSurvivalSeconds != null) {
    return formatDuration(entry.bestSurvivalSeconds);
  }
  return "-";
}

function formatDuration(value) {
  if (value == null) {
    return "-";
  }
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) {
    return "-";
  }
  const totalSeconds = Math.max(0, Math.round(numeric));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const minutesPart = minutes > 0 ? `${minutes}m` : "";
  const secondsPart = `${seconds}s`;
  return `${minutesPart} ${secondsPart}`.trim();
}


function buildRowClassName(rank) {
  const classes = ["zc-table__row"];
  const numericRank = Number(rank);

  if (Number.isFinite(numericRank)) {
    if (numericRank === 1) {
      classes.push("zc-table__row--first");
    } else if (numericRank === 2) {
      classes.push("zc-table__row--second");
    } else if (numericRank === 3) {
      classes.push("zc-table__row--third");
    }
  }

  return classes.join(" ");
}

export default function LeaderboardSection({ embedded = false }) {
  const [leaderboardItems, setLeaderboardItems] = useState([]);
  const [leaderboardStatus, setLeaderboardStatus] = useState("loading");
  const [leaderboardError, setLeaderboardError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [searchStatus, setSearchStatus] = useState("idle");
  const [searchError, setSearchError] = useState(null);
  const [searchResults, setSearchResults] = useState([]);

  const [activeStreamer, setActiveStreamer] = useState(null);
  const [profileStatus, setProfileStatus] = useState("idle");
  const [profileError, setProfileError] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const sectionClassName = embedded ? "zc-section zc-section--embedded" : "zc-section";
  const lastSyncedSearchRef = useRef("");

  const updateParams = useCallback((mutator, options = { replace: true }) => {
    setSearchParams(current => {
      const params = new URLSearchParams(current);
      mutator(params);
      return params;
    }, options);
  }, [setSearchParams]);

  const applySearchValue = useCallback(value => {
    updateParams(params => {
      if (value) {
        params.set("search", value);
      } else {
        params.delete("search");
      }
      params.delete("streamer");
      params.delete("handle");
    });
  }, [updateParams]);

  const applyStreamerValue = useCallback(streamer => {
    updateParams(params => {
      if (streamer?.userId) {
        params.set("streamer", streamer.userId);
        if (streamer.username) {
          params.set("handle", streamer.username);
        } else {
          params.delete("handle");
        }
        params.delete("search");
      } else {
        params.delete("streamer");
        params.delete("handle");
      }
    });
  }, [updateParams]);

  useEffect(() => {
    let cancelled = false;

    async function loadLeaderboard() {
      setLeaderboardStatus("loading");
      setLeaderboardError(null);
      try {
        const data = await fetchLeaderboard(DEFAULT_LIMIT);
        if (cancelled) {
          return;
        }
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
          ? data.results
          : Array.isArray(data?.kills)
          ? data.kills
          : [];
        setLeaderboardItems(
          items.map(entry => normalizeStreamer(entry)).filter(Boolean)
        );
        setLeaderboardStatus("success");
      } catch (error) {
        if (cancelled) {
          return;
        }
        setLeaderboardStatus("error");
        setLeaderboardError(
          error.message || "Errore nel caricamento della classifica"
        );
      }
    }

    loadLeaderboard();

    return () => {
      cancelled = true;
    };
  }, []);

  const leaderboardTitle = activeStreamer
    ? "Profilo streamer"
    : "Classifica Zombie Crushers";

  const triggerSearch = useCallback(
    async (query, { updateParams = false } = {}) => {
      const trimmed = (query ?? "").trim();

      if (!trimmed) {
        if (updateParams) {
          applySearchValue("");
        }
        setSearchTerm("");
        setSearchResults([]);
        setSearchStatus("idle");
        setSearchError(null);
        lastSyncedSearchRef.current = "";
        return;
      }

      if (trimmed.length < 2) {
        if (updateParams) {
          applySearchValue(trimmed);
        }
        setSearchTerm(trimmed);
        setSearchResults([]);
        setSearchStatus("idle");
        setSearchError("Inserisci almeno 2 caratteri");
        lastSyncedSearchRef.current = trimmed;
        return;
      }

      if (updateParams) {
        applySearchValue(trimmed);
      }

      setSearchTerm(trimmed);
      setActiveStreamer(null);
      setProfileData(null);
      setProfileError(null);
      setProfileStatus("idle");

      setSearchStatus("loading");
      setSearchError(null);
      try {
        const results = await searchStreamers(trimmed);
        const list = Array.isArray(results)
          ? results
          : Array.isArray(results?.data)
          ? results.data
          : [];
        setSearchResults(list.map(normalizeStreamer).filter(Boolean));
        setSearchStatus("success");
      } catch (error) {
        setSearchStatus("error");
        setSearchError(error.message || "Errore nella ricerca streamer");
      } finally {
        lastSyncedSearchRef.current = trimmed;
      }
    },
    [applySearchValue]
  );

  const handleSelectStreamer = useCallback((streamer, { updateParams: shouldUpdateParams = true } = {}) => {
    if (!streamer?.userId) {
      return;
    }
    if (shouldUpdateParams) {
      applyStreamerValue(streamer);
    }
    setActiveStreamer(streamer);
    setSearchResults([]);
    setSearchStatus("idle");
    setSearchError(null);
    setProfileData(null);
    setProfileError(null);
    setProfileStatus("loading");

    fetchStreamerProfile(streamer.userId)
      .then(data => {
        setProfileData(data);
        setProfileStatus("success");
      })
      .catch(error => {
        setProfileError(error.message || "Impossibile caricare il profilo");
        setProfileStatus("error");
      });
  }, [applyStreamerValue]);

  const handleBackToLeaderboard = () => {
    applyStreamerValue(null);
    setActiveStreamer(null);
    setProfileData(null);
    setProfileError(null);
    setProfileStatus("idle");

    if (searchTerm.trim().length >= 2) {
      triggerSearch(searchTerm, { updateParams: true });
    }
  };

  const showLeaderboard = !activeStreamer;

  useEffect(() => {
    const paramSearch = searchParams.get("search");

    if (paramSearch != null) {
      setSearchTerm(paramSearch);
      const trimmed = paramSearch.trim();

      if (trimmed.length >= 2) {
        if (lastSyncedSearchRef.current !== trimmed) {
          triggerSearch(trimmed, { updateParams: false });
        }
      } else if (trimmed.length === 0) {
        setSearchResults([]);
        setSearchStatus("idle");
        setSearchError(null);
        lastSyncedSearchRef.current = "";
      } else {
        setSearchResults([]);
        setSearchStatus("idle");
        setSearchError("Inserisci almeno 2 caratteri");
        lastSyncedSearchRef.current = trimmed;
      }
    } else {
      setSearchTerm("");
      setSearchResults([]);
      setSearchStatus("idle");
      setSearchError(null);
      lastSyncedSearchRef.current = "";
    }
  }, [searchParams, triggerSearch]);

  useEffect(() => {
    const streamerId = searchParams.get("streamer");

    if (!streamerId) {
      if (activeStreamer) {
        setActiveStreamer(null);
        setProfileData(null);
        setProfileError(null);
        setProfileStatus("idle");
      }
      return;
    }

    if (activeStreamer?.userId === streamerId) {
      return;
    }

    const handleParam =
      searchParams.get("handle") || searchParams.get("username") || activeStreamer?.username || "Streamer";

    handleSelectStreamer(
      { userId: streamerId, username: handleParam },
      { updateParams: false }
    );
  }, [searchParams, activeStreamer, handleSelectStreamer]);

  return (
    <section className={sectionClassName} aria-label="Classifica streamer Zombie Crushers">
      <div className="zc-section__header">
        <div>
          <h2>{leaderboardTitle}</h2>
          {showLeaderboard ? (
            searchTerm.trim().length >= 2 && searchStatus !== "idle" ? (
              <p>Risultati per "{searchTerm}".</p>
            ) : (
              <p>Top {DEFAULT_LIMIT} streamer per uccisioni e sopravvivenza.</p>
            )
          ) : (
            <p>Visualizza i dettagli e gli achievement dello streamer selezionato.</p>
          )}
        </div>
      </div>

      {searchError && (
        <p className="zc-feedback error">{searchError}</p>
      )}

      {showLeaderboard ? (
        <>
          {searchStatus === "loading" && (
            <p className="zc-feedback info">Ricerca in corso...</p>
          )}

          {searchStatus === "success" && searchResults.length > 0 && (
            <div className="zc-search-results" aria-live="polite">
              <h3>Risultati ricerca</h3>
              <ul>
                {searchResults.map(result => (
                  <li key={`${result.userId}-${result.rank ?? "search"}`}>
                    <button
                      type="button"
                      onClick={() => handleSelectStreamer(result)}
                    >
                      <span className="name">{result.username}</span>
                      {result.maxKills != null && (
                        <span className="detail">{result.maxKills} kill max</span>
                      )}
                      {result.bestSurvivalSeconds != null && (
                        <span className="detail">
                          {formatDuration(result.bestSurvivalSeconds)} sopravvivenza
                        </span>
                      )}
                      {result.bestSurvivalTime && result.bestSurvivalSeconds == null && (
                        <span className="detail">{result.bestSurvivalTime}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {searchStatus === "success" && searchResults.length === 0 && searchTerm && (
            <p className="zc-feedback">Nessuno streamer trovato per "{searchTerm}".</p>
          )}

          <div className="zc-leaderboard">
            {leaderboardStatus === "loading" && (
              <p className="zc-feedback info">Caricamento classifica...</p>
            )}

            {leaderboardStatus === "error" && (
              <p className="zc-feedback error">{leaderboardError}</p>
            )}

            {leaderboardStatus === "success" && leaderboardItems.length > 0 && (
              <div className="zc-table" role="table" aria-label="Top streamer">
                <div className="zc-table__header" role="row">
                  <span role="columnheader">#</span>
                  <span role="columnheader">Streamer</span>
                  <span role="columnheader">Record kill</span>
                  <span role="columnheader">Record sopravvivenza</span>
                  <span role="columnheader">Sessioni</span>
                </div>
                {leaderboardItems.map(item => {
                  const rowClassName = buildRowClassName(item.rank);
                  const rankLabel = item.rank != null ? item.rank : "-";

                  return (
                    <button
                      key={item.userId ?? item.username}
                      type="button"
                      className={rowClassName}
                      role="row"
                      data-rank={item.rank ?? undefined}
                      onClick={() => handleSelectStreamer(item)}
                    >
                      <span role="cell" className="zc-table__cell zc-table__rank">
                        <span className="zc-table__rank-badge">{rankLabel}</span>
                      </span>
                      <span role="cell" className="zc-table__cell zc-table__identity">
                        {item.avatar ? (
                          <img src={item.avatar} alt="" aria-hidden="true" />
                        ) : (
                          <span className="avatar-placeholder" aria-hidden="true">
                            {item.username.charAt(0).toUpperCase()}
                          </span>
                        )}
                        <span className="zc-table__name">{item.username}</span>
                      </span>
                      <span
                        role="cell"
                        className="zc-table__cell zc-table__metric"
                        data-label="Kills"
                      >
                        {item.maxKills != null ? item.maxKills : "-"}
                      </span>
                      <span
                        role="cell"
                        className="zc-table__cell zc-table__metric"
                        data-label="Time"
                      >
                        {formatSurvival(item)}
                      </span>
                      <span
                        role="cell"
                        className="zc-table__cell zc-table__metric"
                        data-label="Sessions"
                      >
                        {item.totalSessions != null ? item.totalSessions : "-"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}

            {leaderboardStatus === "success" && leaderboardItems.length === 0 && (
              <p className="zc-feedback">La classifica non e disponibile al momento.</p>
            )}
          </div>
        </>
      ) : (
        <div className="zc-profile-wrapper">
          <button
            type="button"
            className="zc-back"
            onClick={handleBackToLeaderboard}
          >
            <span className="zc-back__icon" aria-hidden="true">
              {"\u2190"}
            </span>
            <span className="zc-back__label">Torna alla classifica</span>
          </button>

          {profileStatus === "loading" && (
            <p className="zc-feedback info">Caricamento profilo...</p>
          )}

          {profileStatus === "error" && (
            <p className="zc-feedback error">{profileError}</p>
          )}

          {profileStatus === "success" && (
            <StreamerProfilePanel profile={profileData} fallback={activeStreamer} />
          )}
        </div>
      )}
    </section>
  );
}


